import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Heart, BookOpen, ExternalLink, Users, TrendingUp, Sparkles } from "lucide-react";
import Navigation from "@/components/Navigation";

const BlogPage = () => {
  const posts = [
    {
      title: "My Story: Surviving Two Narcissistic Relationships Back to Back",
      excerpt: "I wrote this essay in college, never thinking anyone else would read it. Now it has been downloaded by thousands. Here is why I decided to share the most painful chapter of my life with strangers.",
      tag: "Personal Story",
      link: "/free-guide",
      cta: "Read & Download Free Essay",
      featured: true,
    },
    {
      title: "5 Signs You Are Being Love Bombed (And What to Do About It)",
      excerpt: "He brings you flowers every day. She texts you fifty times before noon. It feels amazing, but something in your gut says slow down. Trust that feeling. Here are the five warning signs we all miss.",
      tag: "Education",
      link: "/resources",
      cta: "Learn More",
    },
    {
      title: "Why Going No-Contact Is So Hard (And Why It Works)",
      excerpt: "We know. Cutting ties with someone you loved feels like losing a limb. But here is the science behind why no-contact is the single most effective strategy for recovery.",
      tag: "Recovery Strategy",
      link: "/bonus",
      cta: "Get the Free Guide",
    },
    {
      title: "Meet Hope: The Free AI Recovery Companion That Listens at 2 AM",
      excerpt: "We built Hope because we know what it feels like to be alone at 2 AM with racing thoughts and nobody to call. She is not a therapist. She is something else entirely.",
      tag: "New Feature",
      link: "/chat",
      cta: "Talk to Hope (Free)",
    },
    {
      title: "Understanding Arkansas Domestic Abuse Laws: What You Need to Know",
      excerpt: "From the Domestic Abuse Act to HB 1829, Arkansas has been strengthening protections for survivors. Here is a plain-language breakdown of your rights.",
      tag: "Legal Resources",
      link: "/resources",
      cta: "View Full Resources",
    },
    {
      title: "\"Sunshine After the Rain\" - Stories of Survival That Will Move You",
      excerpt: "Four stories. Three happy endings. One tragedy. This e-novella captures what so many of us have lived but so few dare to tell. Available in the Growth and Premium plans.",
      tag: "Featured Product",
      link: "/checkout",
      cta: "Get Access",
    },
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Navigation />

      <section className="pt-28 pb-24 px-6 lg:px-12" data-testid="blog-section">
        <div className="container mx-auto max-w-5xl">
          {/* Header */}
          <div className="text-center mb-12">
            <p className="text-sm font-medium uppercase tracking-widest text-[#A69BE0] mb-3">The ThryvLife Blog</p>
            <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-[#1A2A3A] mb-4">
              Stories, Strategies, & Support
            </h1>
            <p className="text-base text-[#1A2A3A]/60 max-w-xl mx-auto">
              Real talk about recovery. Written by someone who has been there. No fluff, no empty promises. Just honest words from us to you.
            </p>
          </div>

          {/* Featured Post */}
          {posts.filter(p => p.featured).map((post, i) => (
            <Card key={i} className="bg-gradient-to-br from-[#4FB3D4]/5 to-[#DCD6F7]/10 rounded-2xl border-2 border-[#4FB3D4]/20 overflow-hidden mb-10" data-testid="featured-post">
              <CardContent className="p-8 md:p-10">
                <div className="inline-flex items-center gap-2 bg-[#4FB3D4]/10 text-[#4FB3D4] px-3 py-1 rounded-full text-xs font-semibold mb-4">
                  <Sparkles className="h-3 w-3" /> {post.tag}
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-semibold text-[#1A2A3A] mb-3">
                  {post.title}
                </h2>
                <p className="text-[#1A2A3A]/60 leading-relaxed mb-6">{post.excerpt}</p>
                <Link to={post.link}>
                  <Button className="bg-[#4FB3D4] hover:bg-[#4FB3D4]/90 text-white rounded-full px-6 py-4 font-medium" data-testid="featured-post-btn">
                    {post.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}

          {/* Posts Grid */}
          <div className="grid md:grid-cols-2 gap-6">
            {posts.filter(p => !p.featured).map((post, i) => (
              <Card key={i} className="bg-white rounded-xl shadow-card border border-[#E5E7EB]/50 overflow-hidden hover:shadow-floating transition-all" data-testid={`blog-post-${i}`}>
                <CardContent className="p-6">
                  <div className="inline-flex items-center gap-1 text-xs font-semibold text-[#A69BE0] mb-3">
                    {post.tag}
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-[#1A2A3A] mb-2">{post.title}</h3>
                  <p className="text-sm text-[#1A2A3A]/60 leading-relaxed mb-4">{post.excerpt}</p>
                  <Link to={post.link} className="inline-flex items-center gap-1 text-sm font-medium text-[#4FB3D4] hover:text-[#4FB3D4]/80 transition-colors" data-testid={`blog-link-${i}`}>
                    {post.cta} <ArrowRight className="h-3 w-3" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 lg:px-12 bg-[#1A2A3A] text-white">
        <div className="container mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="font-heading text-2xl font-bold">ThryvLife</div>
            <nav className="flex gap-6">
              <Link to="/" className="text-white/60 hover:text-white text-sm transition-colors">Home</Link>
              <Link to="/resources" className="text-white/60 hover:text-white text-sm transition-colors">Resources</Link>
              <Link to="/chat" className="text-white/60 hover:text-white text-sm transition-colors">AI Companion</Link>
            </nav>
            <p className="text-white/40 text-xs">&copy; 2025 ThryvLife</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogPage;
